/**
 * Network-edge privacy guard for product analytics (IC-132, ADR-5).
 *
 * `beforeSend` is wired as posthog-js's `before_send`, so it runs on every
 * event the SDK is about to transmit, including events the SDK generates on
 * its own. It fails closed: anything not in the catalog, and any property not
 * allowlisted, is dropped. It never throws.
 */
import type { CaptureResult } from 'posthog-js';
import { EVENT_PROP_KEYS, isCatalogEvent, type AnalyticsEventName } from './events';

const MAX_STRING_LENGTH = 64;

/**
 * SDK-generated `$` properties allowed on the wire. Adding one requires a test
 * edit in `sanitize.test.ts` (ADR-5). Nova URLs carry only the customer's Nova
 * host and `/app`, so `$current_url` / `$pathname` hold no data; revisit if a
 * route ever carries data in the URL.
 */
export const SDK_PROPERTY_ALLOWLIST: ReadonlySet<string> = new Set([
  '$browser',
  '$browser_version',
  '$os',
  '$os_version',
  '$device_type',
  '$screen_height',
  '$screen_width',
  '$viewport_height',
  '$viewport_width',
  '$lib',
  '$lib_version',
  '$host',
  '$current_url',
  '$pathname',
  '$session_id',
  '$window_id',
  '$insert_id',
  '$time',
  '$groups',
  '$device_id',
  '$is_identified',
  '$process_person_profile',
  '$sent_at',
  // identify / group bookkeeping
  '$set',
  '$set_once',
  '$anon_distinct_id',
  '$group_type',
  '$group_key',
  '$group_set',
]);

/**
 * Fields posthog-js needs to transmit an event at all: `token` is the public,
 * write-only project key and `distinct_id` is the anonymous or hashed person
 * id. Neither carries user data. Dropping either makes the SDK silently discard
 * every event, so they always pass (as scalars, like any other property).
 */
export const TRANSPORT_PROPERTY_KEYS: ReadonlySet<string> = new Set(['token', 'distinct_id']);

/** Registered (non-`$`) super-properties set by `initAnalytics` / `setAnalyticsSite`. */
export const SUPER_PROPERTY_KEYS: ReadonlySet<string> = new Set([
  'app_version',
  'environment',
  'customer_key',
  'site_id',
]);

/** Keys that may appear inside the person / group property bags. */
const BAG_KEY_ALLOWLIST: ReadonlySet<string> = new Set([...SDK_PROPERTY_ALLOWLIST, 'name']);
const BAG_PROPERTIES = new Set(['$set', '$set_once', '$group_set']);
/** `$groups` maps group type to group key; `customer` is the only group type Nova uses. */
const GROUP_TYPE_ALLOWLIST: ReadonlySet<string> = new Set(['customer']);

type Bag = Record<string, unknown>;

function isScalar(value: unknown): value is string | number | boolean {
  if (typeof value === 'string') return value.length <= MAX_STRING_LENGTH;
  return typeof value === 'number' || typeof value === 'boolean';
}

function isBag(value: unknown): value is Bag {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function warnDropped(event: string, keys: string[]): void {
  if (import.meta.env.DEV && keys.length > 0) {
    console.warn(`[analytics] dropped from ${event}: ${keys.join(', ')}`);
  }
}

/** Keep only allowlisted keys of a person / group bag, with scalar values. */
function filterBag(bag: Bag, allowed: ReadonlySet<string> = BAG_KEY_ALLOWLIST): Bag {
  const out: Bag = {};
  for (const [key, value] of Object.entries(bag)) {
    if (allowed.has(key) && isScalar(value)) out[key] = value;
  }
  return out;
}

/** Keep only catalog props for `event`, with scalar values of at most 64 chars. */
export function sanitizeProps(event: AnalyticsEventName, props: Bag): Bag {
  const allowed: readonly string[] = EVENT_PROP_KEYS[event];
  const out: Bag = {};
  const dropped: string[] = [];
  for (const [key, value] of Object.entries(props)) {
    if (allowed.includes(key) && isScalar(value)) out[key] = value;
    else dropped.push(key);
  }
  warnDropped(event, dropped);
  return out;
}

function sanitizeSdkProperty(key: string, value: unknown): unknown {
  if (BAG_PROPERTIES.has(key)) return isBag(value) ? filterBag(value) : undefined;
  if (key === '$groups') return isBag(value) ? filterBag(value, GROUP_TYPE_ALLOWLIST) : undefined;
  return isScalar(value) ? value : undefined;
}

function buildProperties(event: string, props: Bag, catalogEvent: AnalyticsEventName | null): Bag {
  const out: Bag = catalogEvent ? sanitizeProps(catalogEvent, props) : {};
  const dropped: string[] = [];
  for (const [key, value] of Object.entries(props)) {
    if (key in out) continue;
    if (key.startsWith('$') && SDK_PROPERTY_ALLOWLIST.has(key)) {
      const clean = sanitizeSdkProperty(key, value);
      if (clean === undefined) dropped.push(key);
      else out[key] = clean;
    } else if ((TRANSPORT_PROPERTY_KEYS.has(key) || SUPER_PROPERTY_KEYS.has(key)) && isScalar(value)) {
      out[key] = value;
    } else if (key.startsWith('$') || !catalogEvent) {
      dropped.push(key);
    }
  }
  warnDropped(event, dropped);
  return out;
}

/** posthog-js `BeforeSendFn`: returns the sanitized event, or null to drop it. */
export function beforeSend(result: CaptureResult | null): CaptureResult | null {
  try {
    if (!result) return null;
    const { event } = result;
    const isBookkeeping = event === '$identify' || event === '$groupidentify';
    const catalogEvent = isCatalogEvent(event) ? event : null;
    if (!catalogEvent && !isBookkeeping) {
      warnDropped(String(event), ['<event>']);
      return null;
    }
    const sanitized: CaptureResult = {
      uuid: result.uuid,
      event,
      properties: buildProperties(event, result.properties ?? {}, catalogEvent),
    };
    if (result.timestamp) sanitized.timestamp = result.timestamp;
    if (isBookkeeping) {
      if (isBag(result.$set)) sanitized.$set = filterBag(result.$set);
      if (isBag(result.$set_once)) sanitized.$set_once = filterBag(result.$set_once);
    }
    return sanitized;
  } catch {
    return null;
  }
}
